export const INSIGHT_METHODS = new Set([
  'rule',
  'statistical',
  'predictive_model',
  'language_model',
]);

const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null && value !== '');

export const normalizeMethod = (method) => {
  const normalized = String(method || 'rule').toLowerCase().replace(/[\s-]+/g, '_');
  return INSIGHT_METHODS.has(normalized) ? normalized : 'rule';
};

export const normalizeConfidence = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.max(0, Math.min(100, numeric <= 1 ? numeric * 100 : numeric));
};

export const normalizeEvidence = (evidence) => {
  if (!evidence) return [];
  const values = Array.isArray(evidence)
    ? evidence
    : Object.entries(evidence).map(([label, value]) => ({ label, value }));

  return values.map((item, index) => {
    if (typeof item === 'string' || typeof item === 'number') {
      return { id: index, label: '', value: String(item), source: '' };
    }
    return {
      id: firstDefined(item.id, index),
      label: firstDefined(item.label, item.name, item.metric, item.field, ''),
      value: firstDefined(item.value, item.description, item.detail, item.text, ''),
      source: firstDefined(item.source, item.source_label, item.sourceLabel, ''),
      observedAt: firstDefined(item.observed_at, item.observedAt, item.as_of, item.asOf, ''),
    };
  }).filter((item) => item.label || item.value || item.source);
};

export const normalizeInsight = (insight, index = 0) => {
  if (!insight || typeof insight !== 'object') {
    return {
      id: `insight-${index}`,
      title: '',
      summary: '',
      titleKey: '',
      summaryKey: '',
      summaryValues: {},
      detail: '',
      recommendation: '',
      category: 'general',
      priority: 'info',
      method: 'rule',
      confidence: null,
      generatedAt: '',
      evidence: [],
      feedback: '',
      forecast: [],
    };
  }
  const detailPayload = insight.detail && typeof insight.detail === 'object' && !Array.isArray(insight.detail)
    ? insight.detail
    : {};
  const detailText = firstDefined(
    detailPayload.description,
    detailPayload.explanation,
    detailPayload.reasoning,
    detailPayload.markdown,
    detailPayload.content,
    typeof insight.detail === 'string' ? insight.detail : '',
    insight.details,
    insight.explanation,
    insight.reasoning,
    ''
  );
  const explicitEvidence = firstDefined(
    insight.evidence,
    insight.supporting_evidence,
    insight.supportingEvidence,
    detailPayload.evidence,
    detailPayload.supporting_evidence,
    detailPayload.supportingEvidence
  );
  const narrativeKeys = new Set([
    'description', 'explanation', 'reasoning', 'markdown', 'content',
    'recommendation', 'recommended_action', 'recommendedAction', 'action',
    'evidence', 'supporting_evidence', 'supportingEvidence',
    'confidence', 'confidence_score', 'confidenceScore',
    'forecast', 'forecast_points', 'forecastPoints', 'chart_data', 'chartData',
  ]);
  const detailEvidence = explicitEvidence || Object.entries(detailPayload)
    .filter(([key]) => !narrativeKeys.has(key))
    .map(([label, value]) => ({
      label: label.replace(/_/g, ' '),
      value: typeof value === 'object' ? JSON.stringify(value) : value,
    }));

  return {
    ...insight,
    id: firstDefined(insight.id, insight.insight_id, insight.insightId, `insight-${index}`),
    title: firstDefined(insight.title, insight.label, insight.name, insight.category, ''),
    summary: firstDefined(insight.summary, insight.message, insight.description, ''),
    titleKey: firstDefined(insight.title_key, insight.titleKey, detailPayload.title_key, detailPayload.titleKey, ''),
    summaryKey: firstDefined(
      insight.summary_key,
      insight.summaryKey,
      detailPayload.summary_key,
      detailPayload.summaryKey,
      ''
    ),
    summaryValues: firstDefined(
      insight.summary_values,
      insight.summaryValues,
      detailPayload.summary_values,
      detailPayload.summaryValues,
      {}
    ),
    detail: detailText,
    recommendation: firstDefined(
      insight.recommendation,
      insight.recommended_action,
      insight.recommendedAction,
      insight.action,
      detailPayload.recommendation,
      detailPayload.recommended_action,
      detailPayload.recommendedAction,
      detailPayload.action,
      ''
    ),
    category: firstDefined(insight.category, insight.type, insight.insight_type, insight.insightType, 'general'),
    priority: firstDefined(insight.priority, insight.severity, insight.level, 'info'),
    method: normalizeMethod(firstDefined(
      insight.method,
      insight.method_type,
      insight.methodType,
      insight.source_type,
      insight.sourceType
    )),
    confidence: normalizeConfidence(firstDefined(
      insight.confidence,
      insight.confidence_score,
      insight.confidenceScore,
      detailPayload.confidence,
      detailPayload.confidence_score,
      detailPayload.confidenceScore
    )),
    generatedAt: firstDefined(
      insight.generated_at,
      insight.generatedAt,
      insight.updated_at,
      insight.updatedAt,
      insight.created_at,
      insight.createdAt,
      ''
    ),
    evidence: normalizeEvidence(detailEvidence),
    feedback: firstDefined(insight.feedback, insight.user_feedback, insight.userFeedback, ''),
    forecast: firstDefined(
      insight.forecast,
      insight.forecast_points,
      insight.forecastPoints,
      insight.chart_data,
      insight.chartData,
      detailPayload.forecast,
      detailPayload.forecast_points,
      detailPayload.forecastPoints,
      detailPayload.chart_data,
      detailPayload.chartData,
      []
    ),
  };
};

export const normalizeInsightsResponse = (payload) => {
  const rawInsights = Array.isArray(payload)
    ? payload
    : firstDefined(payload?.insights, payload?.items, payload?.data, []);
  const insights = Array.isArray(rawInsights) ? rawInsights.map(normalizeInsight) : [];
  const topLevelForecast = firstDefined(
    payload?.seasonal_forecast,
    payload?.seasonalForecast,
    payload?.forecast,
    []
  );
  const embeddedForecast = insights.find((insight) => Array.isArray(insight.forecast) && insight.forecast.length)?.forecast;

  return {
    insights,
    forecast: Array.isArray(topLevelForecast) && topLevelForecast.length
      ? topLevelForecast
      : (embeddedForecast || []),
    generatedAt: firstDefined(payload?.generated_at, payload?.generatedAt, payload?.updated_at, ''),
    role: firstDefined(payload?.role, payload?.audience, ''),
  };
};

export const getPriorityTone = (priority) => {
  const value = String(priority || '').toLowerCase();
  if (['critical', 'urgent', 'high', 'error'].includes(value)) return 'error';
  if (['medium', 'warning', 'warn'].includes(value)) return 'warning';
  if (['low', 'success', 'opportunity'].includes(value)) return 'success';
  return 'info';
};
